import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { Box, Typography } from '@mui/material';
import {
  GridColDef,
  GridFilterModel,
  GridToolbarFilterButton,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import {
  Estate_EstateListDataGridPageFilter,
  Estate_FieldsFragment,
  EstateListDataGridColumns,
  EstateType,
  useEstate_GetAllEstatesForEstateListDataGridLazyQuery,
} from 'queries';

import Modal from 'common/modal';
import { ServerEnabledDataGrid } from 'common/server-enabled-data-grid';
import { GridStateKey } from 'utils/grid-state';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';
import { defaultPaginationModel } from 'utils/pagination-model';

import { useStyles } from './portal-favorite-tasks-select-estate-modal.styles';

type PortalFavoriteTasksSelectEstateModalProps = {
  setShowModal: (value: boolean) => void;
  showModal: boolean;
  estateType: EstateType;
  onSelectEstate: (estate: Estate_FieldsFragment) => void;
};

export const PortalFavoriteTasksSelectEstateModal = ({
  setShowModal,
  showModal,
  estateType,
  onSelectEstate,
}: PortalFavoriteTasksSelectEstateModalProps) => {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();
  const gridApiRef = useGridApiRef();

  const [
    getAllEstatesForEstateListDataGridLazyQuery,
    getAllEstatesForEstateListDataGridLazyQueryResult,
  ] = useEstate_GetAllEstatesForEstateListDataGridLazyQuery({
    variables: {
      input: {
        portalId: portalId,
        paginationInput: {
          pageFilter: [
            {
              column: EstateListDataGridColumns.IsArchived,
              operator: 'equals',
              value: 'false',
            },
          ],
          page: defaultPaginationModel.page,
          pageSize: 10,
        },
      },
    },
    onError: (e) => {
      showAlert({
        message: 'Error finding estates by portal ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const baseFilterModelFromDataGridTab: GridFilterModel = {
    items: [
      {
        field: EstateListDataGridColumns.IsArchived,
        operator: 'equals',
        value: 'false',
      },
      {
        field: EstateListDataGridColumns.Type,
        operator: 'equals',
        value: estateType,
      },
    ],
  };

  const memoizedColumns = useMemo(() => {
    const columns: GridColDef<Estate_FieldsFragment>[] = [];

    columns.push({
      flex: 2,
      disableColumnMenu: true,
      field: EstateListDataGridColumns.EstateName,
      headerName: 'Client Matter',
      filterable: true,
      renderHeader: () => (
        <Typography className={classes.headerItem}>
          <WorkOutlineIcon className={classes.headerIcon} fontSize="small" /> Client Matter
        </Typography>
      ),
      renderCell: ({ row }) => <Typography className={classes.item}>{row.estateName}</Typography>,
    });

    return columns;
  }, [classes]);

  const getEstateListGridToolbar = () => {
    return (
      <Box className={classes.buttonContainer}>
        <GridToolbarFilterButton />
      </Box>
    );
  };

  return (
    <Modal
      setShowModal={setShowModal}
      onExitClick={() => setShowModal(false)}
      showModal={showModal}
      showActions={false}
      content={
        <Box className={classes.container}>
          <h2>Select an Estate</h2>
          <div>
            <ServerEnabledDataGrid<Estate_EstateListDataGridPageFilter>
              className={classes.dataGrid}
              loading={getAllEstatesForEstateListDataGridLazyQueryResult.loading}
              columns={memoizedColumns}
              apiRef={gridApiRef}
              gridStateKey={GridStateKey.EstateList}
              queryResult={getAllEstatesForEstateListDataGridLazyQueryResult}
              executeLazyQuery={getAllEstatesForEstateListDataGridLazyQuery}
              baseFilterModelFromDataGridTab={baseFilterModelFromDataGridTab}
              disableColumnReorder
              disableRowSelectionOnClick
              disableColumnMenu
              columnHeaderHeight={45}
              autoHeight={true}
              pagination
              onRowClick={(params) => onSelectEstate(params.row as Estate_FieldsFragment)}
              pageSizeOptions={[10, 25, 50, 100]}
              slots={{
                toolbar: getEstateListGridToolbar,
              }}
            />
          </div>
        </Box>
      }
      loading={false}
      actionDisabled={false}
      maxWidth="sm"
    />
  );
};
